import React from 'react'
import {TypedElement} from '../../../entities/project'
import {Transformer as TransformerRef} from 'konva/lib/shapes/Transformer'
import {NodeConfig, Node as NodeRef} from 'konva/lib/Node'
import {TransformerManager} from '../transformerLayers/TransformerManager'
import {canvasElementsRegistry} from '../elements/CanvasElementsRegistry'
import useImage from 'use-image'
import {instance as elementTypeService} from '../../../services/ElementTypeService'

export type CanvasEditorShapeProps = {
  element: TypedElement
  isSelected: boolean
  isTwiceSelected: boolean
  onSelect: () => void
  onChange?: (modified: TypedElement) => void
  onTwiceClick: () => void
  isEditable: boolean
  isSelectable: boolean
  setCursor: (cursor: string) => void
  resetCursor: () => void
  rotatable: boolean
  transformerLayerProps?: any
}

export function CanvasEditorShape(props: CanvasEditorShapeProps) {
  const shapeRef = React.useRef<NodeRef>()
  const transformerRef = React.useRef<TransformerRef>()

  React.useEffect(() => {
    if (props.isSelected) {
      // we need to attach transformer manually
      transformerRef.current?.nodes([shapeRef.current])
      transformerRef.current?.getLayer().batchDraw()
      transformerRef.current?.zIndex?.(1000)
    }
  }, [props.isSelected])

  React.useEffect(() => {
    const handler = () => props.onTwiceClick()
    shapeRef.current?.on('dblclick dbltap', handler)

    return () => {
      if (shapeRef.current) {
        shapeRef.current.off('dblclick dbltap', handler)
      }
    }
  }, [props.onTwiceClick])

  const elementInfo = elementTypeService.getElementType(props.element.type)
  const templateInfo = elementInfo.templates?.[props.element.template]
  const displayProps = templateInfo
    ? {
        //image: templateInfo.src,
        // fill: '',
        fillPatternImage: templateInfo.src,
        // fillPatternRepeat: 'no-repeat',
        // fillPatternX: 0,
        // fillPatternY: 0,
        // fillPatternOffsetX: 0,
        // fillPatternoffsetY: 0,
      }
    : elementInfo?.getShapeFromDisplayProps(props.element.props)
  let [fillPatternImage] = useImage(displayProps?.fillPatternImage)
  let [templateImage] = useImage(displayProps?.image)

  if (templateInfo && fillPatternImage) {
    displayProps.fillPatternScale = {
      x: props.element.props.width / fillPatternImage.width,
      y: props.element.props.height / fillPatternImage.height,
    }
  }

  if (!props.element.shapeType) {
    return null
  }

  let canvasShapeType = canvasElementsRegistry[props.element.shapeType]?.canvasShapeType

  if (!canvasShapeType) {
    return null
  }

  if (!elementInfo) {
    return null
  }

  let fill = displayProps.fill
  if (props.isTwiceSelected) {
    fill = 'blue'
  }

  const shapeProps: NodeConfig = {
    ...props.element.props,
    draggable: props.isEditable && props.isSelected,
    ...displayProps,
    fill,
    fillPatternImage,
    image: templateImage,
    onClick:
      props.isEditable || props.isSelectable
        ? () => {
            props.onSelect()
            props.setCursor('move')
          }
        : undefined,
    onDoubleClick: props.isEditable ? props.onTwiceClick : undefined,
    ref: shapeRef,
    onMouseEnter:
      props.isEditable || props.isSelectable
        ? () => {
            if (props.isSelected) {
              props.setCursor('move')
            } else {
              props.setCursor('pointer')
            }
          }
        : undefined,

    // zIndex: elementInfo.zIndex || 10,
    onMouseLeave: props.resetCursor,
  }

  return (
    <>
      {React.createElement(canvasShapeType, shapeProps)}

      {props.isSelected && props.isEditable && (
        <TransformerManager
          element={props.element}
          mode="edit"
          transformerRef={transformerRef}
          shapeRef={shapeRef}
          onChange={props.onChange}
          transformerLayerProps={props.transformerLayerProps}
        ></TransformerManager>
      )}

      {props.isSelected && !props.isEditable && props.isSelectable && (
        <TransformerManager
          element={props.element}
          mode="selection"
          transformerRef={transformerRef}
          shapeRef={shapeRef}
          onChange={props.onChange}
          transformerLayerProps={props.transformerLayerProps}
        ></TransformerManager>
      )}
    </>
  )
}
